import { useState } from "react";
import { MarkdownBody } from "./MarkdownBody";
import { cleanChatMarkdown } from "@/lib/cleanText";

interface ItineraryCardSummary {
  dayCount: number;
  dayTitles: string[];
}

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  itinerarySummary?: ItineraryCardSummary;
  onViewItinerary?: () => void;
}

export function ChatMessage({
  role,
  content,
  itinerarySummary,
  onViewItinerary,
}: ChatMessageProps) {
  const isUser = role === "user";

  if (!isUser && itinerarySummary) {
    return (
      <ItineraryCard
        summary={itinerarySummary}
        prose={content}
        onViewItinerary={onViewItinerary}
      />
    );
  }

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3 min-w-0`}>
      <div
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words [overflow-wrap:anywhere] ${
          isUser
            ? "bg-gradient-to-r from-accent to-electric text-deep rounded-br-md font-medium whitespace-pre-wrap"
            : "frost-card text-ink rounded-bl-md"
        }`}
      >
        {isUser ? content : <MarkdownBody content={cleanChatMarkdown(content)} />}
      </div>
    </div>
  );
}

function ItineraryCard({
  summary,
  prose,
  onViewItinerary,
}: {
  summary: ItineraryCardSummary;
  prose: string;
  onViewItinerary?: () => void;
}) {
  const [showProse, setShowProse] = useState(false);
  const cleanedProse = cleanChatMarkdown(prose);

  return (
    <div className="flex justify-start mb-3 min-w-0">
      <div className="w-full max-w-[95%] rounded-2xl rounded-bl-md border border-accent/40 bg-gradient-to-br from-accent/15 via-accent/5 to-transparent p-4 space-y-2 [overflow-wrap:anywhere]">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="text-lg">🗺</span>
          <span className="text-sm font-semibold text-ink">
            Itinerary {summary.dayCount === 1 ? "updated" : `drafted — ${summary.dayCount} days`}
          </span>
        </div>

        <ul className="space-y-0.5 text-sm text-ink">
          {summary.dayTitles.slice(0, 7).map((title, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-accent-light font-semibold shrink-0">
                Day {i + 1}
              </span>
              <span className="text-ink-light truncate">{title}</span>
            </li>
          ))}
          {summary.dayTitles.length > 7 && (
            <li className="text-xs text-ink-light pl-0.5">
              …and {summary.dayTitles.length - 7} more
            </li>
          )}
        </ul>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {onViewItinerary && (
            <button
              type="button"
              onClick={onViewItinerary}
              className="text-xs font-semibold text-deep bg-accent hover:bg-accent-light rounded-md px-2.5 py-1 transition-colors"
            >
              View on timeline →
            </button>
          )}
          {cleanedProse && (
            <button
              type="button"
              onClick={() => setShowProse((v) => !v)}
              aria-expanded={showProse}
              className="text-xs text-ink-light hover:text-ink underline"
            >
              {showProse ? "Hide notes" : "Show notes"}
            </button>
          )}
        </div>

        {showProse && cleanedProse && (
          <div className="pt-2 border-t border-white/10">
            <MarkdownBody content={cleanedProse} />
          </div>
        )}
      </div>
    </div>
  );
}
